import { Button, cx, Skeleton, useToast } from "@ml/ui";
import { ArrowUpRight, Lock, MessageCircle, UserRound } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { canChangePlanInPortal, useBillingPortal, useBillingSummary } from "../../lib/billing";
import { usePlan, type LockReason } from "../../lib/plan";
import "./plans.css";

/** Opens the in-app assistant on its "Get support" form (features/assistant/AssistantWidget.tsx). */
export function openSupportChat() {
  window.dispatchEvent(new CustomEvent("gf-help-assistant", { detail: { panel: "support" } }));
}

/**
 * The one way to ask for more. A direct customer with a subscription changes plan in Stripe's
 * billing portal; a direct customer without one talks to us; a reseller's customer asks their account
 * manager (the reseller sets their plan). Operators get nothing here.
 */
export function PlanCta({ reason = "upgrade", className }: { reason?: LockReason; className?: string }) {
  const { user } = useAuth();
  const { operator } = usePlan();
  const direct = !!user && !operator && !user.parentId;
  // A feature switched off for the account isn't a plan question: no need to ask billing.
  const billing = useBillingSummary(direct && reason === "upgrade");
  const portal = useBillingPortal();
  const toast = useToast();
  if (!user || operator) return null;
  if (user.parentId) {
    return (
      <p className={cx("plans-cta-note", className)}>
        <UserRound size={16} aria-hidden="true" />
        <span>{reason === "switched_off" ? "Ask your account manager to switch it on." : "Ask your account manager to change your plan."}</span>
      </p>
    );
  }
  if (reason === "upgrade" && billing.isLoading) {
    return <Skeleton width={190} height={38} radius={6} className={className} />;
  }
  if (reason === "upgrade" && canChangePlanInPortal(billing.data)) {
    return (
      <Button
        variant="primary"
        icon={<ArrowUpRight size={16} />}
        loading={portal.isPending || portal.isSuccess}
        onClick={() => portal.mutate(undefined, { onError: (err) => toast.error(err) })}
        className={className}
      >
        Change plan
      </Button>
    );
  }
  return (
    <Button variant="primary" icon={<MessageCircle size={16} />} onClick={openSupportChat} className={className}>
      {reason === "switched_off" ? "Talk to us about switching it on" : "Talk to us about upgrading"}
    </Button>
  );
}

/** Small lock after a nav or tab label: the page exists but isn't on this plan. */
export function PlanLock({ className }: { className?: string }) {
  return (
    <span className={cx("plans-lock", className)} role="img" aria-label="Not on your plan" title="Not on your plan">
      <Lock size={12} strokeWidth={2.2} aria-hidden="true" />
    </span>
  );
}
